import { format } from 'date-fns';
import request from '../utils/request';
import { clientbookApi } from '../core/clientbook-api';
import { getRequest, patchRequest } from '../core/apiRequests';

const parseFilenameFromContentDisposition = (header, fallback) => {
  if (!header) return fallback;
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8Match) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      // fall through
    }
  }
  const quotedMatch = /filename="([^"]+)"/i.exec(header);
  if (quotedMatch) return quotedMatch[1];
  const bareMatch = /filename=([^;]+)/i.exec(header);
  if (bareMatch) return bareMatch[1].trim();
  return fallback;
};

const getRolexInquiryExport = async (path, query, fallbackFilename) => {
  const response = await request({
    url: path,
    method: 'GET',
    params: query,
    responseType: 'blob',
  });
  return {
    blob: response.data,
    filename: parseFilenameFromContentDisposition(response.headers?.['content-disposition'], fallbackFilename),
  };
};

export const exportRolexInquiryDetail = (query = {}) =>
  getRolexInquiryExport('/rolex-inquiries/export/detail', query, 'rolex-inquiries-detail.csv');

export const exportRolexInquirySummary = (query = {}) =>
  getRolexInquiryExport('/rolex-inquiries/export/summary', query, 'rolex-inquiries-summary.zip');

export const getVendorById = async vendorId => {
  const body = await clientbookApi.get({ url: `/vendors/${vendorId}` });
  return { data: body };
};

export const getVendors = async ({ params = {} }) => {
  const limit = Number(params.limit) || 20;
  const offset = Number(params.offset || 0);
  const query = { offset, limit };
  if (params.enabled !== undefined) query.enabled = params.enabled;

  const body = await clientbookApi.get({ url: '/vendors', query });
  const page = offset > 0 ? Math.ceil(offset / limit) + 1 : 1;
  const pageCount = Math.floor((body?.total || 0) / (body?.limit || limit));
  return { data: { ...body, page, pageCount } };
};

export const getVendorItems = async (vendorId, { params = {} }) => {
  const limit = Number(params.limit) || 20;
  const page = params.page ? Number(params.page) : undefined;
  const offset = page !== undefined ? (page - 1) * limit : Number(params.offset || 0);
  const computedPage = page !== undefined ? page : Math.max(1, Math.ceil(offset / limit) + (offset === 0 ? 1 : 0));
  const searchTerm = params.search || '';

  let body;
  if (searchTerm) {
    body = await clientbookApi.post({
      url: '/searches/vendors/items',
      data: { vendorIds: [vendorId], searchTerm, offset, limit },
    });
  } else {
    body = await clientbookApi.get({ url: `/vendors/${vendorId}/items`, query: { offset, limit } });
  }

  const pageCount = Math.floor((body?.total || 0) / (body?.limit || limit));
  return { data: { ...body, page: computedPage, pageCount } };
};

export const getVendorItemById = async (vendorId, itemId) => {
  const body = await clientbookApi.get({ url: `/vendors/${vendorId}/items/${itemId}` });
  const pageCount = body?.total != null && body?.limit != null ? Math.floor(body.total / body.limit) : undefined;
  return { data: pageCount !== undefined ? { ...body, pageCount } : body };
};

export const getVendorItem = async itemId => {
  const body = await clientbookApi.get({ url: `/vendoritems/${itemId}` });
  return { data: body };
};

export const addVendorItemToClientCollection = (clientId, collectionId, data) => {
  return request({
    url: `/clients/${clientId}/collections/${collectionId}/vendorItems`,
    method: 'POST',
    data,
  });
};

export const trackVendorEvent = async data => {
  const eventDateTime = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
  const body = {
    merchant_id: data.merchantId,
    associate_id: data.associateId,
    vendor_id: data.vendorId,
    mpn: data.mpn,
    event_type: data.eventType,
    event_date_time: eventDateTime,
    event_source: data.eventSource || 'product list',
  };
  return clientbookApi.post({ url: '/events/vendor', data: body });
};

export const searchVendorItems = ({
  vendorIds,
  query = '',
  offset = 0,
  limit = 20,
  collections,
  families,
  materials,
  excludeCategories,
}) => {
  const body = { vendorIds, searchTerm: query, offset, limit, archived: false };
  if (collections?.length > 0) body.collections = collections;
  if (families?.length > 0) body.families = families;
  if (materials?.length > 0) body.materials = materials;
  if (excludeCategories?.length > 0) body.excludeCategories = excludeCategories;
  return clientbookApi.post({
    url: '/searches/vendors/items',
    data: body,
  });
};

export const getVendorItemCollections = vendorIds => {
  return clientbookApi.post({
    url: '/searches/vendors/items/collections',
    data: { vendorIds },
  });
};

export const getVendorItemFamilies = vendorIds => {
  return clientbookApi.post({
    url: '/searches/vendors/items/families',
    data: { vendorIds },
  });
};

export const getVendorItemMaterials = vendorIds => {
  return clientbookApi.post({
    url: '/searches/vendors/items/materials',
    data: { vendorIds },
  });
};

// --- Inquiry API ---

export const listInquiries = (query = {}) => {
  return clientbookApi.get({
    url: '/inquiries',
    query,
    loading: false,
  });
};

export const getInquiryById = id => {
  return clientbookApi.get({
    url: `/inquiries/${id}`,
    loading: false,
  });
};

export const createInquiry = data => {
  return clientbookApi.post({
    url: '/inquiries',
    data,
    loading: false,
  });
};

export const updateInquiry = (id, data) => {
  return clientbookApi.put({
    url: `/inquiries/${id}`,
    data,
    loading: false,
  });
};

export const deleteInquiry = id => {
  return clientbookApi.delete({
    url: `/inquiries/${id}`,
    loading: false,
  });
};

export const getInquiriesByClient = clientId => {
  return clientbookApi.get({
    url: `/inquiries/clients/${clientId}`,
    loading: false,
  });
};

export const getInquiriesByVendorItem = vendorItemId => {
  return clientbookApi.get({
    url: `/inquiries/vendor-items/${vendorItemId}`,
    loading: false,
  });
};

// --- Vendor Settings (dashboard API) ---

export const getVendorSettings = vendorId => {
  return getRequest({
    params: ['vendors', vendorId, 'settings'],
    loadingStatusOff: true,
  });
};

export const updateVendorSettings = (vendorId, data) => {
  return patchRequest({
    params: ['vendors', vendorId, 'settings'],
    data,
    loadingStatusOff: true,
  });
};
